import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useFragment } from 'react-relay/hooks';
import { appSubscriptionCheck$key } from 'src/components/__generated__/appSubscriptionCheck.graphql';
import { appSubscriptionCheckFragment } from 'src/components/appSubscriptionCheck';
import { FeatureFlagNames } from 'src/generated/feature-flags';
import { useAppState } from 'src/shared/appState';
import { useFeatureFlagWithConditions } from 'src/shared/feature-flags';
import { useAccountData } from 'src/shared/hooks/useAccountData';

interface AgentAssistantContextValue {
    isEnabled: boolean;
    isOpen: boolean;
    isFullScreen: boolean;
    animateFullScreenEntry: boolean;
    openSidebar: () => void;
    openAiMode: () => void;
    openClassicMode: () => void;
    close: () => void;
    changeFullScreen: (isFullScreen: boolean) => void;
}

const AgentAssistantContext = createContext<AgentAssistantContextValue | null>(null);

export const AgentAssistantProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const { userInfo } = useAccountData();
    const { queryData } = useAppState();
    const subscriptionCheck = useFragment<appSubscriptionCheck$key>(appSubscriptionCheckFragment, queryData?.appSubscriptionCheck ?? null);
    const [isOpen, setIsOpen] = useState(false);
    const [isFullScreen, setIsFullScreen] = useState(false);
    const [animateFullScreenEntry, setAnimateFullScreenEntry] = useState(false);
    const featureFlagContext = { userId: userInfo?.userID?.toString() };
    // appSubscriptionCheck queries only active subscriptions; a trial is included in this result.
    const hasActiveSubscription = Boolean(subscriptionCheck?.subscription && !subscriptionCheck.subscription.isCancelled);
    const isEnabledByFeatureFlag = useFeatureFlagWithConditions(FeatureFlagNames.BOAIAGENT, featureFlagContext);
    const isEnabled = isEnabledByFeatureFlag && hasActiveSubscription;
    const shouldStartWithAiAgent = useFeatureFlagWithConditions(FeatureFlagNames.START_WITH_AIAGENT, featureFlagContext);

    const openSidebar = useCallback(() => {
        setAnimateFullScreenEntry(false);
        setIsFullScreen(false);
        setIsOpen(true);
    }, []);

    const openAiMode = useCallback(() => {
        setAnimateFullScreenEntry(true);
        setIsFullScreen(true);
        setIsOpen(true);
    }, []);

    const openClassicMode = useCallback(() => {
        setAnimateFullScreenEntry(false);
        setIsFullScreen(false);
        setIsOpen(false);
    }, []);

    const close = useCallback(() => {
        if (isFullScreen) {
            openClassicMode();
            return;
        }

        setIsOpen(false);
    }, [isFullScreen, openClassicMode]);

    const changeFullScreen = useCallback((fullScreen: boolean) => {
        setAnimateFullScreenEntry(fullScreen);
        setIsFullScreen(fullScreen);
    }, []);

    useEffect(() => {
        if (!isEnabled) {
            setAnimateFullScreenEntry(false);
            setIsFullScreen(false);
            setIsOpen(false);
            return;
        }

        if (shouldStartWithAiAgent) {
            setAnimateFullScreenEntry(false);
            setIsFullScreen(true);
            setIsOpen(true);
        }
    }, [isEnabled, shouldStartWithAiAgent]);

    const value = useMemo<AgentAssistantContextValue>(
        () => ({ isEnabled, isOpen, isFullScreen, animateFullScreenEntry, openSidebar, openAiMode, openClassicMode, close, changeFullScreen }),
        [animateFullScreenEntry, changeFullScreen, close, isEnabled, isFullScreen, isOpen, openAiMode, openClassicMode, openSidebar]
    );

    return <AgentAssistantContext.Provider value={value}>{children}</AgentAssistantContext.Provider>;
};

export const useAgentAssistant = () => {
    const context = useContext(AgentAssistantContext);
    if (!context) throw new Error('useAgentAssistant must be used within an AgentAssistantProvider');
    return context;
};
